import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { SyncStatusToast } from '../components/SyncStatusToast';

export const useQuoteData = (quoteId) => {
    // Internal states; we wrap setters to track dirty state
    const [headerData, _setHeaderData] = useState(null);
    const [rowData, _setRowData] = useState([]);
    const [addonTexts, _setAddonTexts] = useState([]);
    const [textTemplates, setTextTemplates] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isSyncingToCRM, setIsSyncingToCRM] = useState(false);

    // Dirty-state tracking for local changes
    const [hasLocalChanges, setHasLocalChanges] = useState(false);
    // Avoid marking dirty during controlled loads
    const suppressDirtySetRef = useRef(false);

    const setHeaderData = useCallback((updater) => {
        _setHeaderData(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            if (!suppressDirtySetRef.current) {
                try {
                    if (JSON.stringify(next) !== JSON.stringify(prev)) setHasLocalChanges(true);
                } catch { setHasLocalChanges(true); }
            }
            return next;
        });
    }, []);

    const setRowData = useCallback((updater) => {
        _setRowData(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            if (!suppressDirtySetRef.current) {
                try {
                    if (JSON.stringify(next) !== JSON.stringify(prev)) setHasLocalChanges(true);
                } catch { setHasLocalChanges(true); }
            }
            return next;
        });
    }, []);

    const setAddonTexts = useCallback((updater) => {
        _setAddonTexts(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            if (!suppressDirtySetRef.current) {
                try {
                    if (JSON.stringify(next) !== JSON.stringify(prev)) setHasLocalChanges(true);
                } catch { setHasLocalChanges(true); }
            }
            return next;
        });
    }, []);

    // Allow external code to clear dirty flag after server-driven updates
    const markClean = useCallback(() => setHasLocalChanges(false), []);

    const [associatedCosts, setAssociatedCosts] = useState({ Carriage: '', Duties: '', Packing: '', Services: '', Others: '' });
    const [costDistribution, setCostDistribution] = useState('proportional');

    const fetchQuoteData = useCallback(async (currentQuoteId, showToast = false) => {
        setLoading(true);
        setError(null);
        try {
            const [quoteResponse, templatesResponse] = await Promise.all([
                fetch(`/server/quote_service/`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'get_items_by_crm_id', quoteCrmId: currentQuoteId })
                }),
                fetch(`/server/quote_service/`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'get_text_templates' })
                })
            ]);

            if (!quoteResponse.ok) {
                const errorData = await quoteResponse.json();
                throw new Error(errorData.error || `Server error: ${quoteResponse.status}`);
            }
            if (!templatesResponse.ok) {
                throw new Error('Failed to fetch text templates');
            }

            const quoteResult = await quoteResponse.json();
            const templatesResult = await templatesResponse.json();

            if (!quoteResult || !quoteResult.header) {
                throw new Error("Server did not return valid data for the quote.");
            }

            const fetchedHeader = quoteResult.header;
            if (!fetchedHeader.Valid_Till) {
                const defaultDate = new Date();
                defaultDate.setMonth(defaultDate.getMonth() + 2);
                fetchedHeader.Valid_Till = defaultDate.toISOString().split('T')[0];
            }

            // Controlled set (do not mark as dirty when loading from server)
            suppressDirtySetRef.current = true;
            _setHeaderData(fetchedHeader);
            _setRowData(quoteResult.items || []);
            setTextTemplates(templatesResult);

            let initialAddonTexts = quoteResult.addonTexts || [];
            if (initialAddonTexts.length === 0 && templatesResult?.texts?.length > 0) {
                const defaultTextsToAdd = [];
                const { groups: groupTemplates, texts: allTextTemplates } = templatesResult;
                const preferredLang = quoteResult.header.Preferred_Language || 'en';
                allTextTemplates.forEach(textTemplate => {
                    if (textTemplate.isDefault) {
                        const groupTemplate = groupTemplates.find(g => g.name === textTemplate.parentGroupName);
                        if (groupTemplate) {
                            defaultTextsToAdd.push({
                                id: `new_${textTemplate.id}_${Date.now()}`,
                                Source_Template: { id: textTemplate.id, name: textTemplate.name },
                                Header: textTemplate.header?.[preferredLang] || textTemplate.header?.en || '',
                                Text_Content: textTemplate.content?.[preferredLang] || textTemplate.content?.en || '',
                                Group_Name: groupTemplate.name,
                                Placement: groupTemplate.placement,
                                Sequence: textTemplate.sequence,
                                Is_Detached: false,
                            });
                        }
                    }
                });
                initialAddonTexts = defaultTextsToAdd;
            }
      
            _setAddonTexts(initialAddonTexts);
            setCostDistribution(quoteResult.header.AsCost_Distribution_Method || 'proportional');
            setAssociatedCosts({
                Carriage: quoteResult.header.AsCost_Carriage || '',
                Duties: quoteResult.header.AsCost_Duties || '',
                Packing: quoteResult.header.AsCost_Packing || '',
                Services: quoteResult.header.AsCost_Services || '',
                Others: quoteResult.header.AsCost_Others || ''
            });
            setHasLocalChanges(false);

            if (showToast) {
                toast.info("Changes have been reset to the last saved version.");
            }
        } catch (err) {
            setError(err.message);
            console.error("FetchQuoteData error:", err);
            toast.error(`Error loading data: ${err.message}`);
        } finally {
            suppressDirtySetRef.current = false;
            setLoading(false);
        }
    }, []); 

    useEffect(() => { 
        if (quoteId) { 
            fetchQuoteData(quoteId); 
        } 
    }, [quoteId, fetchQuoteData]);

    // --- KLÍČOVÁ OPRAVA: Vrácení logiky pro rozpočítávání nákladů ---
    useEffect(() => {
        if (!Array.isArray(rowData) || rowData.length === 0) return;

        const totalAssociatedCost = Object.values(associatedCosts).reduce((sum, cost) => sum + (parseFloat(cost) || 0), 0);
        
        // Pokud nejsou žádné náklady, není co počítat
        if (totalAssociatedCost === 0 && rowData.every(item => (item.AsCost_Unit || 0) === 0)) {
            return;
        }

        const totalBaseOP = rowData.reduce((sum, item) => {
            const listPrice = parseFloat(item.List_Price) || 0;
            const opDiscount = parseFloat(item.OP_Discount) || 0;
            const quantity = parseFloat(item.Quantity) || 1;
            return sum + (listPrice * (1 - opDiscount / 100) * quantity);
        }, 0);

        const newRowData = rowData.map(item => {
            let itemAssociatedCost = 0;
            let itemAssociatedCostUnit = 0;
            const quantity = parseFloat(item.Quantity) || 1;

            if (totalAssociatedCost > 0) {
                if (costDistribution === 'equal') {
                    itemAssociatedCost = totalAssociatedCost / rowData.length;
                } else { // Proportional
                    const listPrice = parseFloat(item.List_Price) || 0;
                    const opDiscount = parseFloat(item.OP_Discount) || 0;
                    const itemBaseOP = listPrice * (1 - opDiscount / 100) * quantity;
                    const proportion = totalBaseOP > 0 ? itemBaseOP / totalBaseOP : 0;
                    itemAssociatedCost = totalAssociatedCost * proportion;
                }
            }
            
            if (quantity > 0) {
                itemAssociatedCostUnit = itemAssociatedCost / quantity;
            }

            return { ...item, OP_AsCosts: itemAssociatedCost, AsCost_Unit: itemAssociatedCostUnit };
        });

        // Aktualizujeme stav pouze pokud došlo ke skutečné změně, abychom předešli nekonečné smyčce
        if (JSON.stringify(newRowData.map(d => d.AsCost_Unit)) !== JSON.stringify(rowData.map(d => d.AsCost_Unit))) {
            setRowData(newRowData);
        }
    }, [associatedCosts, costDistribution, rowData, setRowData]);
    // ------------------------------------------------------------------

    const handleSaveChanges = useCallback(async (showNotification = true) => {
        if (!quoteId) {
            toast.error("Missing Quote ID for saving.");
            return false;
        }
        setIsSaving(true);
        setError(null);

        const fullHeaderData = { ...headerData, associatedCosts, costDistribution };

        const headerPromise = fetch('/server/quote_service/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'update_header', quoteCrmId: quoteId, header: fullHeaderData })
        });

        const itemsPromise = fetch('/server/quote_service/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'update_items', quoteCrmId: quoteId, items: rowData })
        });

        const textsPromise = fetch('/server/quote_service/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'update_texts', quoteCrmId: quoteId, addonTexts: addonTexts })
        });

        try {
            const [headerResponse, itemsResponse, textsResponse] = await Promise.all([headerPromise, itemsPromise, textsPromise]);

            if (!headerResponse.ok || !itemsResponse.ok || !textsResponse.ok) {
                let errorMsg = '';
                if (!headerResponse.ok) { errorMsg += `Header Error: ${(await headerResponse.json()).error}. `; }
                if (!itemsResponse.ok) { errorMsg += `Items Error: ${(await itemsResponse.json()).error}. `; }
                if (!textsResponse.ok) { errorMsg += `Texts Error: ${(await textsResponse.json()).error}.`; }
                throw new Error(errorMsg);
            }

            if (showNotification) {
                toast.success('Changes saved successfully!');
            }
            // Local save means CRM is now out of date; mark Items_Synced = false without setting local dirty
            suppressDirtySetRef.current = true;
            _setHeaderData(prev => ({ ...(prev || {}), Items_Synced: false }));
            suppressDirtySetRef.current = false;
            setHasLocalChanges(false);
            return true;
        } catch (err) {
            console.error("Save Changes failed with error:", err);
            setError(err.message);
            toast.error(`Save failed: ${err.message}`);
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [quoteId, rowData, headerData, addonTexts, associatedCosts, costDistribution]);
    
    const handleRefreshItemsFromCRM = useCallback(async () => {
        if (!quoteId) return;
        setIsRefreshing(true);
        try {
          await fetch(`/server/quote_service/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'refresh_items', quoteCrmId: quoteId })
          });
          toast.success('Data successfully reloaded from CRM!');
          await fetchQuoteData(quoteId);
        } catch (err) {
          toast.error(`Reload error: ${err.message}`);
        } finally {
          setIsRefreshing(false);
        }
      }, [quoteId, fetchQuoteData]);

    const handleSyncItemsToCRM = useCallback(async (publishPdf = true) => {
        if (!headerData || !quoteId) {
            toast.error("Cannot sync: Header data or Quote ID is missing.");
            return;
        }
        if (!Array.isArray(rowData) || rowData.length === 0) {
            toast.warn('Please add at least one item before saving to CRM.');
            return;
        }
        const allowedStages = ['Draft', 'Engineering', 'Quotation'];
        if (!allowedStages.includes(headerData.Quote_Stage)) {
            toast.warn(`Sync to CRM is only allowed in 'Draft', 'Engineering', or 'Quotation' stage.`);
            return;
        }
        // Only require WorkDrive when we intend to publish PDF; never refresh items from CRM here
        if (publishPdf && !headerData.Workdrive_Folder_ID) {
            const delay = (ms) => new Promise(r => setTimeout(r, ms));
            const attempts = 3; // quick poll to let Flow finish
            let ready = false;
            for (let i = 0; i < attempts; i++) {
                try {
                    const resp = await fetch('/server/quote_service/', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ action: 'get_or_refresh_workdrive', quoteCrmId: quoteId })
                    });
                    if (resp.ok) {
                        const data = await resp.json();
                        if (data?.ready && data?.folderId) {
                            setHeaderData(prev => ({ ...prev, Workdrive_Folder_ID: data.folderId, Workdrive_Share_Link: data.link }));
                            ready = true;
                            break;
                        }
                    }
                } catch {}
                if (i < attempts - 1) { await delay(1500); }
            }
            if (!ready) {
                toast.info('WorkDrive folder is being prepared. Please try Save to CRM again in a few seconds.', { autoClose: 4000 });
                return;
            }
        }
    
        setIsSyncingToCRM(true);
        setError(null);
    
        const initialStatus = {
            overall: 'running',
            steps: publishPdf
                ? [
                    { label: 'Local Save', status: 'pending', message: '' },
                    { label: 'CRM Synchronization', status: 'pending', message: '' },
                    { label: 'Store PDF in WorkDrive', status: 'pending', message: '' }
                  ]
                : [
                    { label: 'Local Save', status: 'pending', message: '' },
                    { label: 'CRM Synchronization', status: 'pending', message: '' }
                  ]
        };
        
        const toastId = toast(<SyncStatusToast status={initialStatus} />, {
            type: 'info',
            className: 'custom-toast',
            icon: false,
            autoClose: false,
            closeOnClick: false,
            draggable: false,
            closeButton: false,
        });
    
        const updateStatus = (stepIndex, newStatus, newMessage = '') => {
            initialStatus.steps[stepIndex].status = newStatus;
            if (newMessage) {
                initialStatus.steps[stepIndex].message = newMessage;
            }
            toast.update(toastId, { render: <SyncStatusToast status={initialStatus} /> });
        };
    
        try {
            updateStatus(0, 'running');
            const saveSuccess = await handleSaveChanges(false);
            if (!saveSuccess) {
                updateStatus(0, 'error', 'Local save failed');
                throw new Error("Local save failed. Sync process aborted.");
            }
            updateStatus(0, 'success', 'All changes saved');
    
            updateStatus(1, 'running');
            const verifyAfterTimeout = async () => {
                try {
                    const resp = await fetch('/server/quote_service/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ action: 'get_items_by_crm_id', quoteCrmId: quoteId })
                    });
                    if (!resp.ok) return false;
                    const data = await resp.json();
                    const itemsOk = Array.isArray(data.items) && data.items.length >= 0; // zero items can still be valid
                    const headerOk = !!data.header && (data.header.Workdrive_Folder_ID || data.header.Items_Synced !== undefined);
                    return itemsOk && headerOk;
                } catch {
                    return false;
                }
            };
            const syncResponse = await fetch('/server/quote_service/', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'sync_to_crm', quoteCrmId: quoteId, requireWorkDrive: !!publishPdf })
            });
            let syncResult = null;
            if (syncResponse.status === 408) {
                // Gateway timeout: server may still be finishing. Verify by polling quote status
                updateStatus(1, 'running', 'Server timeout, verifying sync status...');
                const attempts = 6;
                const delay = (ms) => new Promise(r => setTimeout(r, ms));
                let verified = false;
                for (let i = 0; i < attempts; i++) {
                    verified = await verifyAfterTimeout();
                    if (verified) break;
                    await delay(1500);
                }
                if (!verified) {
                    updateStatus(1, 'error', 'Verification after timeout failed');
                    throw new Error('CRM sync failed: 408');
                }
                updateStatus(1, 'success', 'Verified after timeout');
                // Bring local state up to date
                await fetchQuoteData(quoteId);
            } else {
                // Normal processing
                try { syncResult = await syncResponse.json(); } catch { syncResult = null; }
                if (!syncResponse.ok) {
                    const errMsg = syncResult?.error || 'Unknown server error';
                    updateStatus(1, 'error', errMsg);
                    throw new Error(syncResult?.error || `CRM sync failed: ${syncResponse.status}`);
                }
                const { stats } = syncResult;
                const syncMsg = `${stats.productsCreated} added, ${stats.productsUpdated} updated. Quote saved.`;
                updateStatus(1, 'success', syncMsg);
            }
    
            if (publishPdf) {
                updateStatus(2, 'running');
                const pdfResponse = await fetch('/server/pdf_service/', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'save_to_workdrive', quoteCrmId: quoteId })
                });
                if (!pdfResponse.ok) {
                    const errorData = await pdfResponse.json();
                    const pdfErrMsg = errorData.details || errorData.error || "Unknown error";
                    updateStatus(2, 'error', pdfErrMsg);
                    throw new Error("Failed to save PDF to WorkDrive.");
                }
                updateStatus(2, 'success', 'PDF stored successfully');
            }
    
            initialStatus.overall = 'done';
            toast.update(toastId, {
                render: <SyncStatusToast status={initialStatus} />,
                type: 'success',
                autoClose: 7000,
                closeButton: true,
            });
            await fetchQuoteData(quoteId);
    
        } catch (err) {
            console.error("Sync to CRM process failed:", err);
            setError(err.message);
            initialStatus.overall = 'error';
            toast.update(toastId, {
                render: <SyncStatusToast status={initialStatus} />,
                type: 'error',
                autoClose: 12000,
                closeButton: true,
            });
        } finally {
            setIsSyncingToCRM(false);
        }
    }, [quoteId, headerData, rowData, handleSaveChanges, fetchQuoteData, setHeaderData]);

    return {
        headerData, setHeaderData,
        rowData, setRowData,
        addonTexts, setAddonTexts,
        textTemplates,
        loading, error, isSaving, isRefreshing, isSyncingToCRM,
        hasLocalChanges, markClean,
        associatedCosts, setAssociatedCosts,
        costDistribution, setCostDistribution,
        fetchQuoteData,
        handleSaveChanges,
        handleRefreshItemsFromCRM,
        handleSyncItemsToCRM
    };
};